/**
 * Labstep
 *
 * @module components/Molecule/Filter
 * @desc Filter molecule by inchi
 */

import { ICONS } from 'labstep-web/constants/icons';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { Button } from 'labstep-web/core/Button';
import type { Ketcher } from 'labstep-web/core/ChemDraw/types';
import { ChemistryEditorKetcher } from 'labstep-web/core/Chemistry/Editor/Ketcher';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import {
  PostFilterExperimentWorkflowMolecule,
  PostFilterMolecule,
} from 'labstep-web/services/postFilter/filters/molecule';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { MoleculeFilterProps } from './types';

export const MoleculeFilter: React.FC<MoleculeFilterProps> = ({
  setParams,
  searchParams,
  addPostFilter,
  viewComponent,
  entityName,
}) => {
  const [editor, setEditor] = useState<Ketcher | null>(null);

  useEffect(() => {
    if (editor) {
      const moleculeInchi: string =
        searchParams.molecule_inchi as string;
      if (moleculeInchi) {
        (editor as Ketcher).setMolecule(moleculeInchi);
      }
    }
  }, [editor, searchParams.molecule_inchi]);

  return (
    <Modal
      header="Chemical Structure"
      size="large"
      onClose={({ toggleModal }) => {
        setEditor(null);
        toggleModal();
      }}
      actions={({ toggleModal }) => (
        <Button
          primary
          onClick={async () => {
            const module = await import(
              'labstep-web/services/molecule.service'
            );
            let inchi;
            inchi = await (editor as Ketcher).getInchi();

            if (inchi) {
              if (inchi.length > 0) {
                if (addPostFilter) {
                  let postFilter:
                    | typeof PostFilterMolecule
                    | typeof PostFilterExperimentWorkflowMolecule =
                    PostFilterMolecule;
                  if (entityName === ExperimentWorkflow.entityName) {
                    postFilter = PostFilterExperimentWorkflowMolecule;
                  }
                  addPostFilter(
                    postFilter.getNode([inchi as string]),
                  );
                } else {
                  setParams({
                    molecule_inchi: inchi,
                  });
                }
              }
            }
            setEditor(null);
            toggleModal();
          }}
        >
          Done
        </Button>
      )}
      content={() => (
        <div>
          <ChemistryEditorKetcher onLoad={setEditor} />
        </div>
      )}
      viewComponent={
        viewComponent ||
        (({ toggleModal }) => (
          <ActionComponent
            type="option"
            icon={ICONS.molecule.primary}
            text="Chemical Structure"
            onClick={toggleModal}
          />
        ))
      }
    />
  );
};
